// src/features/tasks/components/TaskForm/TaskFormEquipmentSection.jsx
import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  IconButton,
  Chip,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Room as RoomIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useEquipment } from '../../../equipment/hooks/useEquipment';
import { getEquipmentImageByModel } from '../../../equipment/utils/getEquipmentImage';
import { CLASSIFICATION_COLORS } from '../../../rooms/utils/classification_colors';
import AddEquipmentDialog from '../TaskDetailsPage/AddEquipmentDialog';
import { FormSection } from '../../../../components/common/form';
import { useConfirmDialog } from '../../../../hooks/mui/useConfirmDialog';

export default function TaskFormEquipmentSection({
  equipment = [],
  onEquipmentAdd,
  onEquipmentRemove,
  loading = false,
  number,
}) {
  const { t } = useTranslation(['tasks', 'equipment', 'rooms', 'common']);
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const { equipment: availableEquipment, fetchDepartmentEquipment } = useEquipment();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [hovered, setHovered] = useState(null);

  const handleOpenDialog = async () => {
    setDialogOpen(true);
    await fetchDepartmentEquipment(false); // Fetch only active equipment
  };

  // Group equipment by room
  const equipmentByRoom = useMemo(() => {
    const grouped = {};
    equipment.forEach((item) => {
      const roomId = item.room?.room_id || 'no_room';
      const roomName = item.room?.name || t('tasks:equipment.no_room', { defaultValue: 'No Room' });

      if (!grouped[roomId]) {
        grouped[roomId] = {
          roomName,
          roomClassification: item.room?.classification,
          items: [],
        };
      }
      grouped[roomId].items.push(item);
    });
    return grouped;
  }, [equipment, t]);

  const linkedEquipmentIds = equipment.map((item) => item.equipment_id);

  const handleAddEquipment = ({ equipments, relation_type, notes, rotation_label, rotation_options }) => {
    const items = equipments.flatMap((equipment_id) => {
      const equipmentDetail = availableEquipment.find(
        (eq) => eq.equipment_id === equipment_id
      );
      return equipmentDetail
        ? [{ equipment_id, relation_type, notes, rotation_label, rotation_options, equipment: equipmentDetail }]
        : [];
    });
    if (items.length > 0) {
      onEquipmentAdd(items);
    }
    setDialogOpen(false);
  };

  const handleDeleteEquipment = (equipmentId, name) => {
    showConfirmDialog({
      title: t('tasks:equipment.remove_title'),
      message: t('tasks:equipment.confirm_remove', { name }),
      confirmText: t('common:buttons.remove'),
      severity: 'error',
      onConfirm: () => onEquipmentRemove(equipmentId),
    });
  };

  return (
    <>
      <ConfirmDialog />
      <FormSection
        number={number}
        title={t('tasks:form.sections.equipment')}
        action={
      <Button
        startIcon={<AddIcon />}
        onClick={handleOpenDialog}
        size="medium"
        variant="outlined"
        disabled={loading}
      >
        {t('tasks:equipment.add_equipment', { defaultValue: 'Add Equipment' })}
      </Button>
        }
      >
          {equipment.length === 0 ? (
            <Alert severity="info">
              {t('tasks:equipment.no_equipment_linked')}
            </Alert>
          ) : (
            <Stack spacing={3}>
              {Object.entries(equipmentByRoom).map(([roomId, roomData]) => (
                <Box key={roomId}>
                  {/* Room Header */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1.5,
                      px: 1,
                    }}
                  >
                    <RoomIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle1" fontWeight="medium">
                      {roomData.roomName}
                    </Typography>
                    {roomData.roomClassification && (
                      <Chip
                        label={t(`rooms:classifications.${roomData.roomClassification}`)}
                        size="small"
                        color={CLASSIFICATION_COLORS[roomData.roomClassification]}
                        variant="outlined"
                      />
                    )}
                  </Box>

                  {/* Equipment in this room */}
                  <Stack spacing={1}>
                    {roomData.items.map((item) => {
                      const isHovered = hovered === item.equipment_id;

                      return (
                        <Box
                          key={item.equipment_id}
                          sx={{
                            p: 2,
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            transition: 'all 0.25s ease-in-out',
                            transform: isHovered ? 'scale(1.01)' : 'scale(1)',
                            boxShadow: isHovered ? 2 : 0,
                            bgcolor: isHovered ? 'action.hover' : 'background.paper',
                          }}
                          onMouseEnter={() => setHovered(item.equipment_id)}
                          onMouseLeave={() => setHovered(null)}
                        >
                          <Box display="flex" alignItems="center">
                            {getEquipmentImageByModel(item.model) && (
                              <Box
                                sx={{
                                  width: '70px',
                                  height: '44px',
                                  backgroundImage: `url(${getEquipmentImageByModel(item.model)})`,
                                  backgroundSize: 'contain',
                                  backgroundPosition: '30% 50%',
                                  backgroundRepeat: 'no-repeat',
                                  mr: 2,
                                }}
                              />
                            )}
                            <Box flex={1} display="flex">
                              <Box>
                                <Typography variant="body1" fontWeight="medium">
                                  {item.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {t(`equipment:equipment_types.${item.equipment_type}`)}
                                  {item.model && ` • ${item.model}`}
                                </Typography>
                              </Box>
                              <Box flex={1} />
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {item.rotation_options?.length > 0 && (
                                  <Chip
                                    label={`${item.rotation_label || t('tasks:equipment.rotation_label', { defaultValue: 'Rotation' })}: ${item.rotation_options[0]}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                                <Chip
                                  label={item.relation_type}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() =>
                                    handleDeleteEquipment(item.equipment_id, item.name)
                                  }
                                  sx={{ ml: 1 }}
                                  disabled={loading}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          </Box>

                          {/* Notes if available */}
                          {item.notes && (
                            <Box sx={{ mt: 1, ml: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                <strong>Notes:</strong> {item.notes}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
      </FormSection>

      <AddEquipmentDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleAddEquipment}
        linkedEquipmentIds={linkedEquipmentIds}
        submitLoading={loading}
      />
    </>
  );
}
